import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { Stethoscope, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@dentos.app");
  const [password, setPassword] = useState("Dentos@2026");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav("/", { replace: true });
    else setErr(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center px-8 py-12 bg-[#FDFDFB]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-md bg-[#0F4C3A] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold text-xl tracking-tight leading-none">DentOS</div>
              <div className="text-[10px] uppercase tracking-widest text-[#52525B] mt-1">Practice Suite</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1C1C1C]">
            Welcome back.
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            Sign in to your clinic workspace. Fast at the front desk, precise in the chair.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                className="w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A] focus:border-[#0F4C3A]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A] focus:border-[#0F4C3A]"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-[#D93838] bg-[#FEF2F2] border border-[#FECACA] rounded-sm px-3 py-2">
                {err}
              </div>
            )}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2.5 rounded-md hover:bg-[#16654F] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          </form>

          <div className="mt-8 p-3 bg-[#F4F4F5] border border-[#E5E5E2] rounded-md text-xs text-[#52525B]">
            <div className="font-semibold text-[#1C1C1C] mb-1">Demo credentials</div>
            <div>admin@dentos.app / Dentos@2026</div>
          </div>
        </div>
      </div>

      {/* Right: brand */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/9062527/pexels-photo-9062527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A3326]/85 via-[#0F4C3A]/70 to-[#0A3326]/90" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[#E27B58] uppercase text-xs tracking-[0.3em] font-semibold">
            Dental Practice OS
          </div>
          <h2 className="mt-4 font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight max-w-md">
            Built India-first. Fast at the chair. DPDP-ready by design.
          </h2>
          <p className="mt-5 text-white/70 max-w-md text-sm leading-relaxed">
            Scheduling, EMR with an FDI-notation tooth chart, GST-compliant billing and audit-grade
            trails — in one keyboard-first workspace.
          </p>
          <div className="mt-10 flex items-center gap-8 text-xs text-white/50 uppercase tracking-widest">
            <span>DPDP • Consent-first</span>
            <span>ABDM-ready</span>
            <span>FDI Notation</span>
          </div>
        </div>
      </div>
    </div>
  );
}
